import {Map} from 'immutable';

import update from 'immutability-helper';
import uniques from "../utils/uniques";
import {defaultSettings} from './settings';
import {itemVoteChanged, loadCollection} from "./utils";
import commentsReducers from "./comments";
import {loadClientSettings} from "./client_settings";

const isPost = (content) => 'comments' in content;
const isComment = (content) => 'parent_id' in content;
const contentListItem = (content) => {
    if (isPost(content)) {
        return { type: 'post', id: content.id };
    }
    if (isComment(content)) {
        return { type: 'comment', id: content.id };
    }
    return null;
};

/**
 * Determines the base community (normalized to lowercase) from given host name.
 * @param host {string}
 * @returns {string}
 */
function baseCommunityFromHost(host) {
    host = host.split(':')[0];
    host = host.toLowerCase();
    if (host.endsWith(".win")) {
        const parts = host.split('.');
        const domain = parts[parts.length - 2];
        if (domain === "patriots") { return "TheDonald"; }
        if (domain !== 'communities') {
            return domain;
        }
    }
    return 'win';
}

export const init = (_state, {host}) => ({
    baseCommunity: baseCommunityFromHost(host),
    passiveLogin: false,
    settings: defaultSettings,
    time: new Date(),
    verbose: process.env.NODE_ENV === 'development' && typeof window !== 'undefined',
    clientSettings: loadClientSettings(),

    lastFeedPos: [],
    googleAdsEnabled: false,

    // Collections
    postMap: Map() /* Map<id:number,Post> */,
    commentMap: Map() /* Map<id:number,Comment> */,
    communityMap: Map() /* Map<name:string,Community> */,

    postLists: Map() /* Map<`${community}:${sort}`],{ids: number[], more: boolean}> */,
    commentLists: Map() /* Map<key:string,{ids:number[], more: boolean}> */,
    contentLists: Map() /* Map<sort:string,{items:{type:'post'|'comment',id:number}[], more: boolean}> */,
    communityLists: Map() /* Map<sort:string,{names: string[]}> */,

    adConsumers: 0,
    adConfig: null,

    feedAdUnits: [],

});

const settingsLoaded = (state, {settings}) =>
    ({...state, settings: {...defaultSettings, ...settings}});

const settingsChanged = (state, {changes}) =>
    ({...state, settings: {...state.settings, ...changes}});

const clientSettingsChanged = (state, {changes}) =>
    ({...state, clientSettings: {...state.clientSettings, ...changes}});

const passiveLoginChanged = (state, {value}) =>
    ({...state, passiveLogin: value});

const timeChanged = (state, {time}) => ({...state, time});

const lastFeedPosChanged = (state, { key, id }) => {
    return { ...state, lastFeedPos: {key, id} };
};

const googleAdsEnabledChanged = (state, { value }) => {
    return { ...state, googleAdsEnabled: value };
};

const userChanged = (state, _action) => ({
    ...state,
    postLists: Map(),
    commentLists: Map(),
    communityLists: Map(),
});

const postsLoaded = ({postMap, ...state}, {posts}) => {
    return {...state, postMap: loadCollection(postMap, posts, 'id')};
};

const feedLoading = ({postLists, ...state}, {key}) => {
    postLists = postLists.update(key, (postList) => ({...postList, loading: true}));
    return ({...state, postLists});
};

const feedLoaded = ({postMap, postLists, ...state}, {key, posts, more}) => {
    postMap = loadCollection(postMap, posts, 'id');
    const ids = uniques(posts.map((post) => post.id));
    postLists = postLists.set(key, {ids, more});
    return {...state, postMap, postLists};
};

const feedTailLoaded = ({postMap, postLists, ...state}, {key, posts, more}) => {
    postMap = loadCollection(postMap, posts, 'id');
    const {ids: oldIds} = postLists.get(key);
    const oldIdSet = new Set(oldIds);
    const newIds = posts.map((post) => post.id).filter((id) => !oldIdSet.has(id));
    postLists = postLists.set(key, {ids: [...oldIds, ...newIds], more});
    return ({...state, postMap, postLists});
};

const feedLoadFailed = ({postLists, ...state}, {key}) => {
    postLists = postLists.update(key, ({ failures, ...postList }) => ({
        ...postList,
        failures: (failures||0) + 1,
    }));
    return ({...state, postLists});
};


const feedLoadDone = ({postLists, ...state}, {key}) => {
    postLists = postLists.update(key, (postList) => ({
        ...postList,
        loading: false,
    }));
    return ({...state, postLists});
};

const feedLoadBailedOut = ({postLists, ...state}, {key}) => {
    postLists = postLists.update(key, (postList) => ({
        ...postList,
        bailOut: true,
    }));
    return ({...state, postLists});
};

const feedLoadReset = ({postLists, ...state}, {key}) => {
    postLists = postLists.delete(key);
    return ({...state, postLists});
};

const feedLoadResetAll = ({postLists, ...state}, {}) => {
    postLists = postLists.clear();
    return ({...state, postLists});
};

const feedsCleared = ({postLists, ...state}, {sort}) => {
    postLists = postLists.withMutations((mutable) => {
        for (const key of postLists.keys()) {
            if (key.split(':')[0] === sort) {
                mutable.delete(key);
            }
        }
    });
    return ({...state, postLists});
};

const feedAdUnitsCleared = (state, { key }) => {
    const { [key]: omitted, ...updatedData } = state.feedAdUnits;
    return { ...state, feedAdUnits: updatedData };
}
const feedAdUnitsChanged = (state, { key, index, data }) => {
    const updatedData = { ...state.feedAdUnits };
    // create the key if it doesn't exist
    if (!(key in updatedData)) {
        updatedData[key] = {};
    }
    // update the value at the specified index
    updatedData[key][index] = data;
    return { ...state, feedAdUnits: updatedData };
}

const countItems = (items, init=null) => {
    const byType = { post: init?.post || 0, comment: init?.comment || 0 };
    for (const item of items) {
        byType[item.type] += 1;
    }
    return byType;
};

const contentListLoaded = ({ postMap, commentMap, contentLists, ...state }, {key, content, more}) => {
    postMap = loadCollection(postMap, content.filter(isPost), 'id');
    commentMap = loadCollection(commentMap, content.filter(isComment), 'id');
    const items = content.map(contentListItem);
    contentLists = contentLists.set(key, {items, more, ...countItems(items)});
    return {...state, postMap, commentMap, contentLists};
};

const contentListExtended = ({ postMap, commentMap, contentLists, ...state }, {key, content, more}) => {
    postMap = loadCollection(postMap, content.filter(isPost), 'id');
    commentMap = loadCollection(commentMap, content.filter(isComment), 'id');
    const items = content.map(contentListItem);
    contentLists = contentLists.update(key, (list) => ({
        items: [...contentLists.get(key).items, ...items], more, ...countItems(items, list)
    }));
    return {...state, postMap, commentMap, contentLists};
};

const communitiesLoaded = ({communityMap, ...state}, {communities, timestamp}) => {
    communities = communities.map((community) => ({ ...community, timestamp }));
    communityMap = loadCollection(communityMap, communities, (c) => c.name.toLowerCase());
    return {...state, communityMap};
};

const communityListLoaded = (state, payload) => {
    state = communitiesLoaded(state, payload);
    const communityLists = state.communityLists.set(payload.sort, {names: payload.communities.map((c) => c.name.toLowerCase())});
    return {...state, communityLists};
};

const postVoteChanged = ({postMap, ...state}, {id, voteState}) => {
    postMap = postMap.set(id, itemVoteChanged(postMap.get(id), voteState));
    return {...state, postMap};
};

const postHiddenChanged = ({postMap, ...state}, {id, hidden}) => {
    const post = postMap.get(id);
    postMap = postMap.set(id, {...post, hidden});
    return {...state, postMap};
};

const postDeleted = ({postMap, ...state}, {id}) => {
    postMap = postMap.set(id, {...postMap.get(id), is_deleted: true});
    return {...state, postMap};
};

const postNsfw = ({postMap, ...state}, {id, newState}) => {
    postMap = postMap.set(id, {...postMap.get(id), is_nsfw: newState});
    return {...state, postMap};
};

const postModDistinguished = ({postMap, ...state}, {id, newState}) => {
    postMap = postMap.set(id, {...postMap.get(id), is_moderator: newState});
    return {...state, postMap};
};

const postSticky = ({postMap, ...state}, {id, newState}) => {
    postMap = postMap.set(id, {...postMap.get(id), is_stickied: newState});
    return {...state, postMap};
};

const postLock = ({postMap, ...state}, {id, newState}) => {
    postMap = postMap.set(id, {...postMap.get(id), is_locked: newState});
    return {...state, postMap};
};

const postRemove = ({postMap, ...state}, {id, moderator}) => {
    postMap = postMap.set(id, update(postMap.get(id), { is_removed: { $set: true }, is_approved: { $set: false },
        moderation: { removed_at: { $set: new Date().valueOf() }, removed_by: { $set: moderator }, approved_at : { $set: 0 }, approved_by: { $set: '' }}}));
    return {...state, postMap};
};

const postApprove = ({postMap, ...state}, {id, moderator}) => {
    postMap = postMap.set(id, update(postMap.get(id), { is_removed: { $set: false }, is_approved: { $set: true },
        moderation: { removed_at: { $set: 0 }, removed_by: { $set: '' }, approved_at : { $set: new Date().valueOf() }, approved_by: { $set: moderator }}}));
    return {...state, postMap};
};

const postIgnoreReports = ({postMap, ...state}, {id, ignoreReports}) => {
    postMap = postMap.set(id, update(postMap.get(id), { moderation: { is_reported: { $set: false }, is_ignored_reports: { $set: ignoreReports }}}));
    return {...state, postMap};
};

const postRetryablePreview = ({postMap, ...state}, {id}) => {
    postMap = postMap.set(id, update(postMap.get(id), { moderation: { is_retryable_preview: { $set: false }}}));
    return {...state, postMap};
};

const postEdited = ({postMap, ...state}, {id, content}) => {
    postMap = postMap.update(
        id,
        (post) => ({...post, content})
    );
    return {...state, postMap};
};

const postFlairChanged = ({postMap, ...state}, {id, flairText, flairClass}) => {
    postMap = postMap.update(
        id,
        (post) => ({...post, post_flair_text: flairText, post_flair_class: flairClass})
    );
    return {...state, postMap};
};

const savedFeedsLoaded = (state, {feeds}) => ({...state, savedFeeds: feeds});

const savedFeedsRemoved = ({savedFeeds, ...state}, {id}) => {
    const newFeeds = savedFeeds.filter((el) => {
        return el.id !== id;
    });
    return {...state, savedFeeds: newFeeds};
};

const savedFeedsAdded = ({savedFeeds, ...state}, {feed}) => {
    savedFeeds.push(feed);
    return {...state, savedFeeds};
};

const savedFeedsUpdated = ({savedFeeds, ...state}, {feed}) => {
    const itemIndex = savedFeeds.findIndex(o => o.id === feed.id);
    if (itemIndex > -1) savedFeeds[itemIndex] = feed;
    return {...state, savedFeeds};
};

const adConsumerAdded = (state, _action) => ({ ...state, adConsumers: state.adConsumers + 1 });
const adConsumerRemoved = (state, _action) => ({ ...state, adConsumers: state.adConsumers - 1 });
const adConfigChanged = (state, {config}) => ({ ...state, adConfig: config });
const adUnitsChanged = (state, {units}) => ({ ...state, adUnits: units });

const actionReducers = {
    init,
    settingsLoaded,
    settingsChanged,
    clientSettingsChanged,
    passiveLoginChanged,
    userChanged,
    timeChanged,
    postsLoaded,
    feedLoading,
    feedLoaded,
    feedTailLoaded,
    feedLoadFailed,
    feedLoadDone,
    feedLoadBailedOut,
    feedLoadReset,
    feedLoadResetAll,
    feedsCleared,
    contentListLoaded,
    contentListExtended,
    communitiesLoaded,
    communityListLoaded,
    postVoteChanged,
    postHiddenChanged,
    postDeleted,
    postEdited,
    postFlairChanged,
    postNsfw,
    postModDistinguished,
    postSticky,
    postLock,
    postRemove,
    postApprove,
    postIgnoreReports,
    postRetryablePreview,
    ...commentsReducers,
    savedFeedsLoaded,
    savedFeedsRemoved,
    savedFeedsAdded,
    savedFeedsUpdated,
    adConsumerAdded,
    adConsumerRemoved,
    adConfigChanged,
    adUnitsChanged,
    lastFeedPosChanged,
    feedAdUnitsCleared,
    feedAdUnitsChanged,
    googleAdsEnabledChanged,
};

const reducer = (state, {type, payload}) => {
    if (state.verbose) {
        // eslint-disable-next-line no-console
        console.log(`ACTION ${type}`, payload);
    }
    let newState = state;
    if (Object.hasOwnProperty.call(actionReducers, type)) {
        try {
            newState = actionReducers[type](state, payload);
        } catch (ex) {
            // eslint-disable-next-line no-console
            console.error(`a reducer for ${type} raised an exception`, ex);
        }
    }
    return newState;
};

export default reducer;
